<template>
  <div class="ficha-widget">
    <!-- Botón flotante con badge -->
    <v-btn
      v-if="!dialog"
      fab
      fixed
      top
      right
      color="primary"
      dark
      large
      elevation="8"
      class="floating-btn"
      @click="abrirDialog"
    >
      <v-badge
        v-if="fichas.length > 0"
        :content="fichas.length"
        color="error"
        overlap
      >
        <v-icon large>mdi-phone-forward</v-icon>
      </v-badge>
      <v-icon v-else large>mdi-phone-forward</v-icon>
    </v-btn>

    <!-- Dialog flotante -->
    <v-dialog v-model="dialog" max-width="480" persistent>
      <v-card>
        <v-card-title class="primary white--text pa-3">
          <v-icon color="white" class="mr-2">mdi-phone-forward</v-icon>
          <span class="subtitle-1">Control de Fichas</span>
          <v-spacer />
          <v-btn icon dark small @click="dialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="pa-3">
          <!-- Alertas -->
          <v-alert v-if="error" type="error" dense dismissible @input="error = ''">
            {{ error }}
          </v-alert>
          
          <v-alert v-if="success" type="success" dense dismissible @input="success = ''">
            {{ success }}
          </v-alert>

          <!-- Ficha Actual -->
          <v-card v-if="fichaActual" color="success" dark class="mb-3">
            <v-card-text class="pa-2">
              <div class="text-h5 font-weight-bold mb-1">{{ fichaActual.codigo }}</div>
              <div class="caption">
                <strong>Estado:</strong> {{ fichaActual.estado }}<br />
                <strong>Caja:</strong> {{ fichaActual.caja || 'N/A' }}
              </div>
            </v-card-text>
            <v-card-actions class="pa-2 pt-0">
              <v-btn 
                color="white" 
                text 
                small 
                block 
                :loading="loading" 
                @click="rellamar(fichaActual.id)"
                class="mb-1"
              >
                <v-icon left small>mdi-phone-sync</v-icon>
                Rellamar
              </v-btn>
              <v-btn 
                color="white" 
                text 
                small 
                block 
                :loading="loading" 
                @click="atender(fichaActual.id)"
                class="mb-1"
              >
                <v-icon left small>mdi-account-check</v-icon>
                Atender
              </v-btn>
              <v-btn 
                color="white" 
                text 
                small 
                block 
                :loading="loading" 
                @click="finalizar(fichaActual.id)"
              >
                <v-icon left small>mdi-check-circle</v-icon>
                Finalizar
              </v-btn>
            </v-card-actions>
          </v-card>

          <!-- Header lista -->
          <div class="d-flex align-center mb-2">
            <span class="subtitle-2">Fichas en Espera</span>
            <v-spacer />
            <v-chip small color="primary">{{ fichas.length }}</v-chip>
          </div>

          <!-- Lista de fichas -->
          <v-list v-if="fichas.length > 0" dense class="ficha-list">
            <v-list-item 
              v-for="(ficha, i) in fichasLimitadas" 
              :key="ficha.id"
              class="pa-1"
            >
              <v-list-item-avatar 
                size="36"
                :color="ficha.tipo === 'preferencial' ? 'warning' : 'primary'"
              >
                <span class="white--text caption">{{ ficha.codigo }}</span>
              </v-list-item-avatar>

              <v-list-item-content>
                <v-list-item-title class="body-2">
                  {{ ficha.codigo }}
                  <v-chip 
                    v-if="ficha.tipo === 'preferencial'" 
                    x-small 
                    color="warning" 
                    class="ml-1"
                  >
                    Pref
                  </v-chip>
                  <v-chip 
                    v-if="i > 0" 
                    x-small 
                    color="grey" 
                    class="ml-1"
                  >
                    Cola
                  </v-chip>
                </v-list-item-title>
              </v-list-item-content>

              <v-list-item-action>
                <v-btn
                  small
                  icon
                  color="primary"
                  :disabled="i > 0"
                  :loading="loading && fichaLlamando === ficha.id"
                  @click="llamar(ficha.id)"
                  :title="i === 0 ? 'Llamar ficha' : 'Debes llamar primero'"
                >
                  <v-icon small>mdi-phone</v-icon>
                </v-btn>
              </v-list-item-action>
            </v-list-item>
          </v-list>

          <div v-else class="text-center caption grey--text py-4">
            No hay fichas en espera
          </div>

          <div v-if="fichas.length > 5" class="text-center caption mt-2">
            +{{ fichas.length - 5 }} fichas más
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>

<script lang="ts">
import { Component, Prop, Vue } from 'vue-property-decorator';
import axios from 'axios';
import io, { Socket } from 'socket.io-client';

interface Ficha {
  id: number;
  codigo: string;
  tipo: string;
  area: string;
  estado: string;
  caja?: string;
  usuarioId?: number;
  createdAt: string;
  fechaLlamado?: string;
}

@Component({
  name: 'LlamarFichas'
})
export default class LlamarFichas extends Vue {
  @Prop({ required: true }) readonly userId!: string | number;

  dialog = false;
  fichas: Ficha[] = [];
  fichaActual: Ficha | null = null;
  loading = false;
  error = '';
  success = '';
  fichaLlamando: number | null = null;
  intervalId: number | null = null;
  socket: Socket | null = null;
  
  apiBase = process.env.VUE_APP_API_FICHAS || 'http://localhost:3013';
  socketUrl = process.env.VUE_APP_SOCKET_FICHAS || 'http://localhost:3013';

  get fichasLimitadas(): Ficha[] {
    return this.fichas.slice(0, 5);
  }

  mounted(): void {
    // Cargar datos iniciales
    this.cargarFichaActual();
    this.cargarFichas();
    
    // Iniciar WebSocket
    this.iniciarWebSocket();
    
    // Auto-actualizar cada 30 segundos (backup por si WebSocket falla)
    // this.intervalId = window.setInterval(() => {
      this.cargarFichaActual();
      this.cargarFichas();
    // }, 30000);
  }

  beforeDestroy(): void {
    if (this.intervalId !== null) {
      clearInterval(this.intervalId);
    }
    
    if (this.socket) {
      this.socket.disconnect();
    }
  }

  iniciarWebSocket(): void {
    // Conectar al namespace /atencion del backend
    this.socket = io(`${this.socketUrl}/atencion`, {
      transports: ['websocket', 'polling'],
    });

    this.socket.on('connect', () => {
      console.log('✅ WebSocket conectado (Cajas)');
    });

    // Cuando se crea una nueva ficha
    this.socket.on('atencion:creada', (ficha: Ficha) => {
      console.log('🔔 Nueva ficha creada:', ficha);
      this.cargarFichas();
    });

    // Cuando alguien llama una ficha (IMPORTANTE: otro cajero la llamó)
    this.socket.on('atencion:llamada', (ficha: Ficha) => {
      console.log('📞 Ficha llamada:', ficha);
      
      // Si es mi ficha, actualizar fichaActual
      if (ficha.usuarioId === Number(this.userId)) {
        this.fichaActual = ficha;
      }
      
      // Remover de la lista de espera (ya fue llamada)
      this.fichas = this.fichas.filter((f) => f.id !== ficha.id);
    });

    // Cuando se actualiza una ficha (atendiendo, finalizada, etc)
    this.socket.on('atencion:actualizada', (ficha: Ficha) => {
      console.log('🔄 Ficha actualizada:', ficha);
      
      // Si es mi ficha actual
      if (this.fichaActual?.id === ficha.id) {
        if (ficha.estado === 'atendido' || ficha.estado === 'cancelado') {
          this.fichaActual = null;
        } else {
          this.fichaActual = ficha;
        }
      }
      
      // Actualizar en la lista de espera
      const index = this.fichas.findIndex((f) => f.id === ficha.id);
      if (index >= 0) {
        if (ficha.estado === 'esperando') {
          this.fichas[index] = ficha;
        } else {
          // Ya no está esperando, remover
          this.fichas.splice(index, 1);
        }
      }
    });

    // Cuando se elimina una ficha
    this.socket.on('atencion:eliminada', (data: { id: number }) => {
      console.log('🗑️ Ficha eliminada:', data.id);
      this.fichas = this.fichas.filter((f) => f.id !== data.id);
      
      if (this.fichaActual?.id === data.id) {
        this.fichaActual = null;
      }
    });

    this.socket.on('disconnect', () => {
      console.log('❌ WebSocket desconectado');
    });

    this.socket.on('error', (error: any) => {
      console.error('Error WebSocket:', error);
    });
  }

  abrirDialog(): void {
    this.dialog = true;
    // Recargar al abrir para tener datos frescos
    this.cargarFichaActual();
    this.cargarFichas();
  }

  async cargarFichas(): Promise<void> {
    try {
      const response = await axios.get(`${this.apiBase}/atencion/hoy`, {
        params: {
          estado: 'esperando',
          user_id: String(this.userId)
        }
      });
      
      this.fichas = response.data;
    } catch (err) {
      console.error('Error cargando fichas:', err);
    }
  }

  async cargarFichaActual(): Promise<void> {
    try {
      const response = await axios.get(`${this.apiBase}/atencion/hoy`, {
        params: {
          user_id: String(this.userId)
        }
      });
      
      const data: Ficha[] = response.data;
      
      // Buscar la última ficha en llamando o atendiendo del usuario actual
      // Filtrar y ordenar por ID DESC para obtener la más reciente
      const fichasDelUsuario = data.filter(
        (f: Ficha) => (f.estado === 'llamando' || f.estado === 'atendiendo') && 
               f.usuarioId === Number(this.userId)
      );
      
      // Ordenar por ID descendente para obtener la más reciente
      if (fichasDelUsuario.length > 0) {
        fichasDelUsuario.sort((a, b) => b.id - a.id);
        this.fichaActual = fichasDelUsuario[0];
      } else {
        this.fichaActual = null;
      }
    } catch (err) {
      console.error('Error cargando ficha actual:', err);
    }
  }

  async llamar(id: number): Promise<void> {
    try {
      this.loading = true;
      this.fichaLlamando = id;
      this.error = '';
      this.success = '';
      
      const response = await axios.patch(`${this.apiBase}/atencion/${id}`, {
        estado: 'llamando',
        usuarioId: Number(this.userId)
      });
      
      const ficha: Ficha = response.data;
      this.success = `Ficha ${ficha.codigo} llamada`;
      this.fichaActual = ficha;
      
      // Ya no es necesario recargar, el WebSocket lo hace automáticamente
      // await this.cargarFichas();
    } catch (err: any) {
      this.error = err.response?.data?.message || err.message || 'Error al llamar ficha';
    } finally {
      this.loading = false;
      this.fichaLlamando = null;
    }
  }

  async atender(id: number): Promise<void> {
    try {
      this.loading = true;
      this.error = '';
      this.success = '';
      
      const response = await axios.patch(`${this.apiBase}/atencion/${id}`, {
        estado: 'atendiendo'
      });
      
      const ficha: Ficha = response.data;
      this.success = `Ficha ${ficha.codigo} en atención`;
      this.fichaActual = ficha;
    } catch (err: any) {
      this.error = err.response?.data?.message || err.message || 'Error al atender';
    } finally {
      this.loading = false;
    }
  }

  async finalizar(id: number): Promise<void> {
    try {
      this.loading = true;
      this.error = '';
      this.success = '';
      
      const response = await axios.patch(`${this.apiBase}/atencion/${id}`, {
        estado: 'atendido'
      });
      
      const ficha: Ficha = response.data;
      this.success = `Ficha ${ficha.codigo} finalizada`;
      this.fichaActual = null;
    } catch (err: any) {
      this.error = err.response?.data?.message || err.message || 'Error al finalizar';
    } finally {
      this.loading = false;
    }
  }

  async rellamar(id: number): Promise<void> {
    try {
      this.loading = true;
      this.error = '';
      this.success = '';
      
      const response = await axios.patch(`${this.apiBase}/atencion/${id}`, {
        estado: 'llamando',
        fechaLlamado: new Date().toISOString()
      });
      
      const ficha: Ficha = response.data;
      this.success = `Ficha ${ficha.codigo} rellamada`;
      this.fichaActual = ficha;
      
      // Emitir evento al WebSocket para que otros vean el rellamado
      if (this.socket?.connected) {
        this.socket.emit('atencion:rellamada', ficha);
      }
    } catch (err: any) {
      this.error = err.response?.data?.message || err.message || 'Error al rellamar';
    } finally {
      this.loading = false;
    }
  }
}
</script>

<style scoped>
.ficha-widget {
  position: relative;
}

.floating-btn {
  position: fixed !important;
  bottom: 89vh !important;
  right: 20px !important;
  top: auto !important;
  z-index: 9999 !important;
}

.floating-btn:hover {
  transform: scale(1.05);
  transition: transform 0.2s ease;
}

.ficha-list {
  max-height: 300px;
  overflow-y: auto;
}
</style>
